import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppSelector } from '../../store';

// PACKAGES
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Alert } from 'reactstrap';
import { useHistory, useParams } from 'react-router-dom';

// COMPONENTS
import Button from '../../components/Button';
import GlobalModal from '../../components/GlobalModal';
import VideoModal from './VideoModal';
import FormCheckBox from '../../components/FormCheckBox';
import LoadingSpinner from '../../components/LoadingSpinner';
import RemoveModal from './RemoveModal';

// ICONS
import { ReactComponent as NoteIcon } from '../../assets/svg/note-icon.svg';

// TYPES
import { TrainingEditModuleCommand } from '../../api/models';
import { RouteParams } from '../../Interfaces';

const TrainingSubmit: React.FC = ({}) => {
	const history = useHistory();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Getting the route id
	const params = useParams<RouteParams>();

	// Get all Standards from Redux store
	const childSafeStandards = useAppSelector((state) => state.lists.childSafeStandards);

	// Set up data state and initial data
	const initialData: TrainingEditModuleCommand = {
		id: undefined,
		order: undefined,
		title: undefined,
		subtitle: undefined,
		overview: undefined,
		videoEmbedCode: undefined,
		selectedChildSafeStandards: []
	};

	const [moduleData, setModuledata] = React.useState<TrainingEditModuleCommand>(initialData);

	// State to control Modal
	const [showVideoModal, setShowVideoModal] = React.useState<boolean>(false);

	const [showRemoveModal, setShowRemoveModal] = React.useState<boolean>(false);

	// State to control confirm Declaration
	const [confirmDeclaration, setConfirmDeclaration] = React.useState<boolean>(false);

	// Function to  Get training modules data from api call
	const getTrainingModulesDetailData = async () => {
		if (params.id) {
			setIsLoading(true);

			const response = await apibridge.getOrganisationNewTrainingModule(params.id);

			if (response.isError) {
				setIsLoading(false);

				setFormErrorMessage(
					response.validationErrors.length
						? validationErrorsToString(response.validationErrors)
						: response.message || 'An error has occurred'
				);
			} else {
				const { result = initialData } = response;

				setIsLoading(false);

				setModuledata(result);
			}
		} else {
			setIsLoading(false);

			setModuledata(initialData);
		}
	};

	React.useEffect(() => {
		getTrainingModulesDetailData();
	}, []);

	const onSubmit = async (values: any) => {
		setIsLoading(true);

		const trainingData: TrainingEditModuleCommand = {
			id: values.id,
			order: values.order,
			title: values.titleInput,
			subtitle: values.subtitleInput,
			overview: values.overviewInput,
			videoEmbedCode: values.videoEmbedCode,
			selectedChildSafeStandards: values.childSafetyStandardsInput
		};

		const response = await apibridge.submitOrganisationNewTrainingModule(trainingData);

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			history.push('/training');
		}
	};

	const validationSchema = Yup.object({
		titleInput: Yup.string().required('Required'),
		subtitleInput: Yup.string().required('Required'),
		overviewInput: Yup.string().required('Required'),
		childSafetyStandardsInput: Yup.array().min(1, 'Required')
	});

	const initialValues = {
		id: moduleData.id || '',
		titleInput: moduleData.title || '',
		subtitleInput: moduleData.subtitle || '',
		overviewInput: moduleData.overview || '',
		videoEmbedCode: moduleData.videoEmbedCode || '',
		childSafetyStandardsInput: moduleData.selectedChildSafeStandards || []
	};

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={onSubmit} enableReinitialize>
			{(formik) => {
				return (
					<Form translate="yes" className="general-form">
						{!!formErrorMessage ? (
							<div className="form-validation-error">{formErrorMessage}</div>
						) : (
							<>
								{isLoading ? (
									<LoadingSpinner show={isLoading} />
								) : (
									<>
										<div className="general-divider mbtom-40"></div>

										<Alert color="info">
											<div className="alert-icon">
												<NoteIcon />
											</div>

											<div className="alert-content">
												<p>
													<strong>
														Please carefully review any content prior to adding it to your ChildAware Portal
													</strong>
													. ChildAware is not responsible whatsoever, in any way, for content that is uploaded by
													individual users or organisations including for the accuracy, appropriateness or lawfulness of
													the content.
												</p>
											</div>
										</Alert>

										<div className="general-form-layout-wrapper">
											<div className="form-main">
												<div className="form-group">
													<label htmlFor="titleInput" className="label">
														Training module title
													</label>

													<div className="form-field">
														<Field name="titleInput" id="titleInput" type="text" className="form-input" />

														<ErrorMessage name="titleInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="subtitleInput" className="label">
														Training module subtitle
													</label>

													<div className="form-field">
														<Field name="subtitleInput" id="subtitleInput" type="text" className="form-input" />

														<ErrorMessage name="subtitleInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="overviewInput" className="label">
														Overview text
													</label>

													<div className="form-field">
														<Field
															as="textarea"
															name="overviewInput"
															id="overviewInput"
															type="text"
															className="form-input"
														/>

														<ErrorMessage name="overviewInput" component="div" className="error-message" />
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="videoEmbedCode" className="label">
														Video URL
													</label>

													<p className="sub-grey-text small">
														Supported video formats from either Vimeo or YouTube only.
													</p>

													<div className="form-field">
														<Field
															as="textarea"
															name="videoEmbedCode"
															id="videoEmbedCode"
															type="text"
															className="form-input"
														/>

														<ErrorMessage name="videoEmbedCode" component="div" className="error-message" />

														{formik.values.videoEmbedCode && (
															<Button className="btn-text" onClick={() => setShowVideoModal(true)}>
																Preview video
															</Button>
														)}
													</div>
												</div>

												<div className="form-group">
													<label htmlFor="childSafetyStandardsInput" className="label mbtom-16">
														Which Child Safe Standard(s) does this module cover? Select all that apply
													</label>

													<div className="form-field">
														<FormCheckBox
															values={moduleData.selectedChildSafeStandards || []}
															options={childSafeStandards}
															onChange={(vals: string[]) => formik.setFieldValue('childSafetyStandardsInput', vals)}
														/>

														<ErrorMessage name="childSafetyStandardsInput" component="div" className="error-message" />
													</div>
												</div>
											</div>
										</div>

										<div className="general-divider"></div>

										<div className="form-group mtop-40">
											<label className="form-control-checkbox">
												<input
													type="checkbox"
													checked={confirmDeclaration}
													onChange={() => setConfirmDeclaration((confirmDeclaration) => !confirmDeclaration)}
												/>
												I hereby declare that I am responsible for the provision and distribution of this video media
												content and acknowledge that ChildAware does not hold responsibility for any inappropriate
												material that may be wrongly distributed.
											</label>
										</div>

										<div className="form-two-button-group align-right">
											{params.id ? (
												<Button className="btn-secondary general-form-btn" onClick={() => setShowRemoveModal(true)}>
													Delete training module
												</Button>
											) : null}

											<Button type="submit" className="btn-primary general-form-btn" disabled={!confirmDeclaration}>
												Save
											</Button>
										</div>

										<GlobalModal
											isOpen={showVideoModal}
											setIsOpen={setShowVideoModal}
											titleText="Video embed code preview"
										>
											<VideoModal videoEmbedCode={formik.values.videoEmbedCode || ''} />
										</GlobalModal>

										<GlobalModal
											isOpen={showRemoveModal}
											setIsOpen={setShowRemoveModal}
											titleText="Delete training module"
										>
											<RemoveModal trainingId={params.id} trainingTitle={formik.values.titleInput} />
										</GlobalModal>
									</>
								)}
							</>
						)}
					</Form>
				);
			}}
		</Formik>
	);
};

export default TrainingSubmit;
